import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw, ExternalLink, Settings, PackageCheck, PlayCircle } from 'lucide-react';
import { opsApi } from '../../api/client';

/**
 * Operational monitor for the Active Ants fulfilment integration.
 *
 * Configuration lives in **System & Connectors** — this page only shows
 * live status + sync counters. A deprecation banner at the top of the
 * page points users at the canonical config page.
 *
 * Legacy v2 (API key / account ID) collections are kept around for
 * historical data only; their config form has been removed from the UI.
 */
export default function OpsActiveAnts() {
  const [aaCfg, setAaCfg] = useState(null);
  const [connection, setConnection] = useState(null);
  const [liveStock, setLiveStock] = useState(null);         // v3 snapshot summary
  const [liveShipments, setLiveShipments] = useState(null); // v3 snapshot summary
  const [orderSyncs, setOrderSyncs] = useState([]);
  const [stockSyncs, setStockSyncs] = useState([]);
  // AA outbound kill-switch — read-only signal for the banner.
  const [aaOutboundEnabled, setAaOutboundEnabled] = useState(null);
  // Audit toggles — when ON, the operator sees archived/test rows but
  // those still count as `aaActionable=false` so the pending badge
  // does not inflate.
  const [showArchived, setShowArchived] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [reconciliationFilter, setReconciliationFilter] = useState('all');
  const [syncPlan, setSyncPlan] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncCommitResult, setSyncCommitResult] = useState(null);
  const [syncFilter, setSyncFilter] = useState('updates');
  const [bucket1, setBucket1] = useState(null);
  const [bucket1Loading, setBucket1Loading] = useState(false);
  const [bucket1Filter, setBucket1Filter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const reload = useCallback(async () => {
    const [cfg, conn, oss, sss, outbound] = await Promise.all([
      opsApi.aaV3Config().then(r => r.data).catch(() => null),
      opsApi.aaV3ConnectionTest().then(r => r.data).catch(() => null),
      opsApi.aaOrderSyncs({
        include_archived: showArchived || undefined,
        include_test:     showTest || undefined,
      }).then(r => r.data).catch(() => []),
      opsApi.aaStockSyncs().then(r => r.data).catch(() => []),
      opsApi.aaOutboundStatus().then(r => r.data).catch(() => null),
    ]);
    setAaCfg(cfg);
    setConnection(conn);
    setOrderSyncs(oss);
    setStockSyncs(sss);
    setAaOutboundEnabled(outbound?.outboundEnabled ?? null);

    // Live v3 snapshots — only attempt when the connector authenticated.
    // Either snapshot may fail independently; store the summary + error
    // message so the UI can render a clear live-vs-legacy split.
    if (conn?.tokenAcquired) {
      const [stock, ships] = await Promise.all([
        opsApi.aaV3Stock().then(r => r.data).catch(e => ({ ok: false, message: e?.response?.status === 503 ? 'Unconfigured' : 'Fetch failed' })),
        opsApi.aaV3Shipments().then(r => r.data).catch(e => ({ ok: false, message: e?.response?.status === 503 ? 'Unconfigured' : 'Fetch failed' })),
      ]);
      setLiveStock(stock);
      setLiveShipments(ships);
    } else {
      setLiveStock(null);
      setLiveShipments(null);
    }
  }, [showArchived, showTest]);

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, [reload]);

  const manualRefresh = async () => {
    setRefreshing(true);
    await reload();
    setRefreshing(false);
  };

  const loadReconciliation = async () => {
    setReconciliationLoading(true);
    try {
      const { data } = await opsApi.aaV3StockReconciliation();
      setReconciliation(data);
    } catch (e) {
      setReconciliation({ error: e?.response?.data?.detail || e.message });
    } finally {
      setReconciliationLoading(false);
    }
  };

  const loadSyncDryRun = async () => {
    setSyncBusy(true);
    setSyncCommitResult(null);
    try {
      const { data } = await opsApi.aaStockSyncDryRun({});
      setSyncPlan(data);
    } catch (e) {
      setSyncPlan({ error: e?.response?.data?.detail || e.message });
    } finally {
      setSyncBusy(false);
    }
  };

  const commitSync = async () => {
    if (!syncPlan?.summary) return;
    const eligible = syncPlan.summary.eligibleUpdates;
    if (!window.confirm(
      `Commit AA → commerce stock sync? ${eligible} products will have ` +
      `their products.stock updated to match AA available quantity. ` +
      `Audit log row written per product. Continue?`)) return;
    setSyncBusy(true);
    try {
      const { data } = await opsApi.aaStockSyncCommit({});
      setSyncCommitResult(data);
      // Refresh reconciliation + plan to reflect post-commit state.
      await loadReconciliation();
      await loadSyncDryRun();
    } catch (e) {
      setSyncCommitResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setSyncBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8"><div className="w-8 h-8 border-4 border-gray-200 border-t-gray-800 rounded-full animate-spin" /></div>
    );
  }

  // Summaries derived from legacy-flow sync history (read-only display).
  // `actionableOrders` and `pendingOrders` reflect ONLY rows that are
  // truly safe to push. Archived / prelaunch-test / Mollie-TEST /
  // outbound-disabled / already-synced rows are passive and don't
  // inflate the badge.
  const actionableOrders = orderSyncs.filter(o => o.aaActionable === true).length;
  const syncedOrders = orderSyncs.filter(o => o.syncStatus === 'synced').length;
  const pendingOrders = orderSyncs.filter(
    o => o.aaActionable === true && o.syncStatus === 'not_synced',
  ).length;
  const hiddenAuditCount = orderSyncs.filter(o => o.aaActionable === false).length;
  const discrepancies = stockSyncs.filter(s => s.discrepancy).length;
  const connectorOk = aaCfg?.aaEnabled && aaCfg?.configComplete && connection?.statusReachable && connection?.tokenAcquired;

  return (
    <div data-testid="ops-active-ants" className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Active Ants — Operational Monitor</h1>
          <p className="text-sm text-gray-500 mt-1">Live connector status + historical sync data. Read-only.</p>
        </div>
        <button
          data-testid="aa-monitor-refresh"
          onClick={manualRefresh} disabled={refreshing}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Canonical config redirect banner */}
      <div data-testid="aa-config-banner" className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
        <Settings className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-blue-900 font-semibold">Active Ants credentials are now managed in System &amp; Connectors.</p>
          <p className="text-xs text-blue-700 mt-0.5">
            This page is an operational monitor. Base URL, username, password and mode live on the v3 connector config page.
          </p>
        </div>
        <Link
          data-testid="aa-open-config"
          to="/ops/system"
          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
        >
          Open System &amp; Connectors <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {/* Live connection status */}
      <div data-testid="aa-monitor-status" className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">Connector</p>
          <div className="flex items-center gap-2">
            {connectorOk ? <CheckCircle className="w-4 h-4 text-green-600" /> : (aaCfg?.configComplete ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <XCircle className="w-4 h-4 text-gray-400" />)}
            <span className="text-sm font-semibold text-gray-900">
              {connectorOk ? 'Authenticated' : aaCfg?.configComplete ? 'Configured — test failing' : 'Not configured'}
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-2 space-y-0.5">
            <p>Base URL: <span className="font-mono">{aaCfg?.aaBaseUrl || '—'}</span></p>
            <p>Mode: <span className="font-semibold uppercase">{aaCfg?.aaMode || 'readonly'}</span></p>
            <p>Source: <span className="font-mono">{aaCfg?.source || 'env'}</span></p>
            {connection?.tokenDetail && <p className="text-gray-400 mt-1">{connection.tokenDetail}</p>}
          </div>
        </div>

        <div data-testid="aa-live-stock-card" className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs text-indigo-600 uppercase tracking-wider font-semibold">AA v3 · live stock</p>
          {liveStock?.ok ? (
            <>
              <p className="text-2xl font-bold text-gray-900 mt-1">{liveStock.count} <span className="text-sm font-normal text-gray-400">SKUs</span></p>
              <p className="text-xs text-gray-500 mt-0.5">
                Discrepancies: <span className="font-semibold text-gray-900">{(liveStock.items || []).filter(r => r.discrepancy != null && r.discrepancy !== 0).length}</span>
              </p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-400 mt-1">—</p>
              <p className="text-xs text-gray-400 mt-0.5">{liveStock?.message || (connectorOk ? '…' : 'Connector not ready')}</p>
            </>
          )}
        </div>

        <div data-testid="aa-live-shipments-card" className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs text-indigo-600 uppercase tracking-wider font-semibold">AA v3 · live shipments</p>
          {liveShipments?.ok ? (
            <>
              <p className="text-2xl font-bold text-gray-900 mt-1">{liveShipments.count} <span className="text-sm font-normal text-gray-400">records</span></p>
              <p className="text-xs text-gray-500 mt-0.5">
                Linked locally: <span className="font-semibold text-gray-900">{(liveShipments.items || []).filter(s => s.localOrder).length}</span>
              </p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-400 mt-1">—</p>
              <p className="text-xs text-gray-400 mt-0.5">{liveShipments?.message || (connectorOk ? '…' : 'Connector not ready')}</p>
            </>
          )}
        </div>
      </div>

      {/* Outbound state banner — clearly communicates that the AA
          monitor is currently read-only when the kill-switch is off. */}
      {aaOutboundEnabled === false && (
        <div
          data-testid="aa-outbound-disabled-banner"
          className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Active Ants outbound is DISABLED — read-only monitor.</p>
            <p className="text-xs text-amber-800 mt-0.5">No order push or stock push will be sent. Counters below show <strong>actionable candidates only</strong> for when outbound is re-enabled by operator (kill-switch <code>ACTIVE_ANTS_OUTBOUND_ENABLED</code>).</p>
          </div>
        </div>
      )}

      {/* Legacy history (pre-existing aa_order_syncs / aa_stock_syncs) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="aa-order-sync-card">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Order Sync <span className="ml-1 text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">legacy</span></p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{syncedOrders} <span className="text-sm font-normal text-gray-400">synced</span></p>
          <p className="text-xs text-gray-500 mt-1" data-testid="aa-pending-count">
            <strong>{pendingOrders}</strong> pending <span className="text-gray-400">(actionable only)</span>
          </p>
          {(showArchived || showTest) && hiddenAuditCount > 0 && (
            <p className="text-xs text-amber-600 mt-1" data-testid="aa-audit-hint">
              {hiddenAuditCount} non-actionable row(s) shown for audit
            </p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Stock Sync <span className="ml-1 text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">legacy</span></p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stockSyncs.length} <span className="text-sm font-normal text-gray-400">products</span></p>
          {discrepancies > 0 && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />{discrepancies} discrepancies
            </p>
          )}
        </div>
      </div>

      {/* Recent order sync table — read-only, from legacy flow */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Order Sync Status <span className="ml-2 text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">legacy</span></h2>
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1 text-gray-600 cursor-pointer" data-testid="aa-toggle-show-archived">
              <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="h-3 w-3" />
              Show archived
            </label>
            <label className="flex items-center gap-1 text-gray-600 cursor-pointer" data-testid="aa-toggle-show-test">
              <input type="checkbox" checked={showTest} onChange={e => setShowTest(e.target.checked)} className="h-3 w-3" />
              Show test/retired
            </label>
            <span className="text-gray-400">{actionableOrders} actionable / {orderSyncs.length} shown</span>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-400 uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Order</th>
              <th className="px-4 py-2 text-center">Sync</th>
              <th className="px-4 py-2 text-left">AA Ref</th>
              <th className="px-4 py-2 text-center">Shipment</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {orderSyncs.slice(0, 20).map(o => (
              <tr key={o._id} className={`hover:bg-gray-50/50 ${o.aaActionable === false ? 'bg-gray-50/40 text-gray-500' : ''}`}>
                <td className="px-4 py-2.5 font-mono font-semibold text-gray-900">#{o.orderNumber}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${o.syncStatus === 'synced' ? 'bg-green-100 text-green-700' : o.syncStatus === 'error' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>{o.syncStatus}</span>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{o.aaReference || '—'}</td>
                <td className="px-4 py-2.5 text-center">
                  {o.hasShipment ? <span className="text-xs text-green-600 font-semibold">{o.trackingNumber || 'Yes'}</span> : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-2.5">
                  {o.aaActionable === true ? (
                    <span data-testid="aa-row-actionable" className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">actionable</span>
                  ) : (
                    <div data-testid="aa-row-blocked" className="flex flex-wrap gap-1">
                      {(o.aaBlockedReasons || []).map(r => (
                        <span key={r} className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{r}</span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!orderSyncs.length && <p className="text-center py-6 text-xs text-gray-400 italic">No legacy order-sync history recorded</p>}
      </div>

      {/* Stock reconciliation panel — read-only audit comparing
          local commerce stock against live AA /v3/stock. */}
      <div data-testid="aa-stock-reconciliation" className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">AA Stock Reconciliation</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Compares local <code className="text-[10px]">products.stock</code> (storefront + checkout source-of-truth) against AA <code className="text-[10px]">/v3/stock</code>. Read-only — never writes to either side.
            </p>
          </div>
          <button
            data-testid="reconciliation-load-btn"
            onClick={loadReconciliation} disabled={reconciliationLoading}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3 h-3 ${reconciliationLoading ? 'animate-spin' : ''}`} />
            {reconciliation ? 'Refresh' : 'Run reconciliation'}
          </button>
        </div>

        {reconciliation?.error && (
          <p className="text-sm text-red-700">{reconciliation.error}</p>
        )}

        {reconciliation && reconciliation.totals && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                ['totalProducts', 'Products audited', 'gray'],
                ['aaLiveStockFound', 'Have AA live stock', 'green'],
                ['localZeroAaPositive', 'Local 0 / AA positive', 'amber'],
                ['storefrontOutButAaPositive', 'Storefront says out / AA positive', 'red'],
                ['skuNotFoundInAa', 'SKU not in AA', 'red'],
                ['inactiveWithAaStock', 'Inactive but AA holds stock', 'amber'],
                ['localPositiveAaZero', 'Local positive / AA 0', 'amber'],
                ['ok', 'OK (no discrepancy)', 'green'],
              ].map(([k, label, color]) => {
                const colorMap = {
                  gray: 'bg-gray-50 border-gray-200 text-gray-700',
                  green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                  amber: 'bg-amber-50 border-amber-200 text-amber-800',
                  red: 'bg-red-50 border-red-200 text-red-800',
                };
                return (
                  <div key={k} data-testid={`reco-card-${k}`}
                    className={`border rounded-lg p-3 ${colorMap[color]}`}>
                    <p className="text-[11px] uppercase tracking-wide font-semibold opacity-80">{label}</p>
                    <p className="text-2xl font-bold mt-0.5">{reconciliation.totals[k]}</p>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
              {['all', 'LOCAL_ZERO_AA_POSITIVE', 'SKU_NOT_FOUND_IN_AA',
                  'INACTIVE_WITH_AA_STOCK', 'LOCAL_POSITIVE_AA_ZERO',
                  'MAPPED_BUT_NO_AA_LIVE_STOCK', 'NO_SKU_LOCALLY', 'OK'
                ].map(f => (
                <button key={f}
                  data-testid={`reco-filter-${f}`}
                  onClick={() => setReconciliationFilter(f)}
                  className={`px-2.5 py-1 rounded-full font-mono border ${
                    reconciliationFilter === f
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}>
                  {f === 'all' ? 'all' : f}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-y border-gray-200">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">Product</th>
                    <th className="px-2 py-1.5 text-left font-semibold">SKU</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Local</th>
                    <th className="px-2 py-1.5 text-right font-semibold">AA</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Active</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Storefront</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Discrepancy</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Recommended action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reconciliation.products
                    .filter(r => reconciliationFilter === 'all' || r.discrepancyType === reconciliationFilter)
                    .map(r => (
                      <tr key={r.productId}
                        data-testid={`reco-row-${r.sku || r.productId}`}
                        className="hover:bg-gray-50">
                        <td className="px-2 py-1.5">{r.name_nl || '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-gray-600">{r.sku || '—'}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.localCommerceStock}</td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {r.aaAvailableQty === null ? '—' : r.aaAvailableQty}
                        </td>
                        <td className="px-2 py-1.5">{r.isActive ? '✓' : '✗'}{r.archived ? ' (arch)' : ''}</td>
                        <td className="px-2 py-1.5 text-gray-600">{r.storefrontAvailabilityStatus}</td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${
                            r.discrepancyType === 'OK' ? 'bg-emerald-100 text-emerald-700' :
                            r.discrepancyType === 'LOCAL_ZERO_AA_POSITIVE' ? 'bg-amber-100 text-amber-800' :
                            r.discrepancyType === 'INACTIVE_WITH_AA_STOCK' ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-700'
                          }`}>{r.discrepancyType}</span>
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">{r.recommendedAction}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Stock-sync sub-panel — admin AA → commerce stock writer */}
            <div data-testid="aa-stock-sync" className="mt-6 border-t border-gray-200 pt-4">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Sync AA → commerce stock</h3>
                  <p className="text-[11px] text-gray-500 mt-0.5 max-w-xl">
                    Active Ants is the source of truth for storefront stock. Updates only{' '}
                    <code>products.stock</code>. Skips products that are inactive, archived,
                    have no SKU, or whose SKU is absent from AA. Customer-facing
                    name / price / image / slug / SKU / VAT / isActive are <strong>never</strong> touched.
                  </p>
                  <p data-testid="scheduler-status-note"
                    className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5 max-w-xl">
                    Automatic stock sync is not enabled yet. Use manual preview / commit below.
                    {/* Future: hourly recommended; tighten to 15 minutes before public launch. */}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="sync-dryrun-btn"
                    onClick={loadSyncDryRun} disabled={syncBusy}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50">
                    {syncPlan ? 'Re-run dry-run' : 'Preview AA → commerce stock sync'}
                  </button>
                  {syncPlan?.summary && syncPlan.summary.eligibleUpdates > 0 && (
                    <button
                      data-testid="sync-commit-btn"
                      onClick={commitSync} disabled={syncBusy}
                      className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
                      Commit stock sync ({syncPlan.summary.eligibleUpdates})
                    </button>
                  )}
                </div>
              </div>

              {syncCommitResult?.error && (
                <p className="text-sm text-red-700 mb-2">{syncCommitResult.error}</p>
              )}
              {syncCommitResult?.ok && (
                <p data-testid="sync-commit-success"
                  className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mb-2">
                  ✓ Commit complete — {syncCommitResult.appliedCount} products updated.
                  {syncCommitResult.skippedStale > 0 &&
                    ` ${syncCommitResult.skippedStale} stale rows skipped during defensive re-read.`}
                </p>
              )}
              {syncPlan?.error && (
                <p className="text-sm text-red-700">{syncPlan.error}</p>
              )}

              {syncPlan?.summary && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs">
                    {[
                      ['eligibleUpdates', 'To update', 'emerald'],
                      ['noChange', 'Already in sync', 'gray'],
                      ['skippedSkuMissing', 'SKU not in AA', 'red'],
                      ['skippedInactive', 'Inactive', 'gray'],
                      ['skippedArchived', 'Archived', 'gray'],
                      ['skippedOpsOnly', 'Ops-only SKUs', 'gray'],
                      ['localZeroAaPositive', 'Local 0 → AA +', 'amber'],
                      ['localPositiveAaZero', 'Local + → AA 0', 'amber'],
                    ].map(([k, label, color]) => {
                      const colorMap = {
                        gray: 'bg-gray-50 border-gray-200 text-gray-700',
                        emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                        amber: 'bg-amber-50 border-amber-200 text-amber-800',
                        red: 'bg-red-50 border-red-200 text-red-800',
                      };
                      return (
                        <div key={k} data-testid={`sync-card-${k}`}
                          className={`border rounded-md px-2 py-1.5 ${colorMap[color]}`}>
                          <p className="text-[10px] uppercase font-semibold opacity-80">{label}</p>
                          <p className="text-base font-bold">{syncPlan.summary[k]}</p>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-gray-500 mb-2">
                    Total stock before / planned: <code>{syncPlan.summary.totalStockBefore}</code> →{' '}
                    <code>{syncPlan.summary.totalStockAfterPlanned}</code>
                    {syncPlan.summary.skippedSkuMissing > 0 && (
                      <span className="ml-2 text-red-700">
                        ⚠ {syncPlan.summary.skippedSkuMissing} active products have SKUs absent from AA — review master data.
                      </span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-2 text-[11px]">
                    {[
                      ['updates', 'Updates only'],
                      ['skipped', 'Skipped'],
                      ['sku-missing', 'SKU missing'],
                      ['local-zero-aa-positive', 'Local 0 / AA +'],
                      ['local-positive-aa-zero', 'Local + / AA 0'],
                      ['all', 'All'],
                    ].map(([f, label]) => (
                      <button key={f}
                        data-testid={`sync-filter-${f}`}
                        onClick={() => setSyncFilter(f)}
                        className={`px-2 py-0.5 rounded-full border ${
                          syncFilter === f
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}>{label}</button>
                    ))}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-y border-gray-200">
                        <tr>
                          <th className="px-2 py-1 text-left font-semibold">Product</th>
                          <th className="px-2 py-1 text-left font-semibold">SKU</th>
                          <th className="px-2 py-1 text-right font-semibold">Local</th>
                          <th className="px-2 py-1 text-right font-semibold">AA</th>
                          <th className="px-2 py-1 text-right font-semibold">Δ</th>
                          <th className="px-2 py-1 text-left font-semibold">Action</th>
                          <th className="px-2 py-1 text-left font-semibold">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {syncPlan.products
                          .filter(r => {
                            if (syncFilter === 'all') return true;
                            if (syncFilter === 'updates') return r.action === 'UPDATE_STOCK';
                            if (syncFilter === 'skipped') return r.action.startsWith('SKIP_');
                            if (syncFilter === 'sku-missing') return r.action === 'SKIP_SKU_NOT_FOUND';
                            if (syncFilter === 'local-zero-aa-positive')
                              return r.action === 'UPDATE_STOCK' && r.currentLocalStock === 0 && (r.aaAvailableQty || 0) > 0;
                            if (syncFilter === 'local-positive-aa-zero')
                              return r.action === 'UPDATE_STOCK' && r.currentLocalStock > 0 && (r.aaAvailableQty || 0) === 0;
                            return true;
                          })
                          .map(r => (
                            <tr key={r.productId} data-testid={`sync-row-${r.sku || r.productId}`}>
                              <td className="px-2 py-1">{r.name_nl || '—'}</td>
                              <td className="px-2 py-1 font-mono text-gray-600">{r.sku || '—'}</td>
                              <td className="px-2 py-1 text-right font-mono">{r.currentLocalStock}</td>
                              <td className="px-2 py-1 text-right font-mono">
                                {r.aaAvailableQty === null ? '—' : r.aaAvailableQty}
                              </td>
                              <td className={`px-2 py-1 text-right font-mono font-semibold ${
                                r.delta > 0 ? 'text-emerald-700' :
                                r.delta < 0 ? 'text-red-700' : 'text-gray-400'}`}>
                                {r.action === 'UPDATE_STOCK' ? (r.delta > 0 ? '+' : '') + r.delta : ''}
                              </td>
                              <td className="px-2 py-1">
                                <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${
                                  r.action === 'UPDATE_STOCK' ? 'bg-emerald-100 text-emerald-700' :
                                  r.action === 'NO_CHANGE' ? 'bg-gray-100 text-gray-600' :
                                  r.action === 'SKIP_SKU_NOT_FOUND' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-800'
                                }`}>{r.action}</span>
                              </td>
                              <td className="px-2 py-1 text-gray-500">{r.reason}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <ShipmentReconciliationPanel connectorOk={connectorOk} />

      {/* ─── Bucket-1: Inactive products with AA stock ─────────────── */}
      <Bucket1Panel
        bucket1={bucket1}
        loading={bucket1Loading}
        filter={bucket1Filter}
        setFilter={setBucket1Filter}
        onLoad={async () => {
          setBucket1Loading(true);
          try {
            const { data } = await opsApi.aaBucket1Audit();
            setBucket1(data);
          } catch (e) {
            console.error('Bucket-1 audit failed', e);
            setBucket1({ ok: false, error: e?.message || 'Audit failed' });
          } finally {
            setBucket1Loading(false);
          }
        }}
      />
    </div>
  );
}


/**
 * Admin-triggered shipment reconciliation panel.
 *
 * Reads AA v3 shipments, resolves each to our commerce orderNumber, and
 * updates local `orders` with tracking info + status promotion.
 *
 * Matching rules (mirrored from `services/shipment_reconciliation.py`):
 * - Shipment must have an AA order id AND a tracking number.
 * - Resolving /v3/orders/{id} must yield a non-empty externalOrderNumber.
 * - Local order state must be `confirmed`/`processing` (→ shipped) or
 *   `shipped` (→ tracking refresh only, status untouched).
 * - `pending`/`cancelled`/`refunded`/`delivered` are never touched.
 *
 * Idempotent: a second identical run reports `already-reconciled` for
 * every shipment that was updated previously.
 */
function ShipmentReconciliationPanel({ connectorOk }) {
  const [since, setSince] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [limit, setLimit] = useState(50);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [showAllResults, setShowAllResults] = useState(false);

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const { data } = await opsApi.aaV3ReconcileRuns({ limit: 20 });
      setRuns(data?.runs || []);
    } catch {
      setRuns([]);
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  const run = async () => {
    setRunning(true);
    setLastResult(null);
    setShowAllResults(false);
    try {
      const body = { dryRun, limit };
      if (since) body.since = since;
      const { data } = await opsApi.aaV3ReconcileShipments(body);
      setLastResult(data);
      await loadRuns();
    } catch (e) {
      setLastResult({
        ok: false,
        message: e?.response?.data?.detail || e?.message || 'Reconciliation failed',
        counters: {}, results: [],
      });
    } finally {
      setRunning(false);
    }
  };

  const decisionBadge = (d) => {
    const map = {
      'updated': 'bg-green-100 text-green-700',
      'tracking-updated': 'bg-blue-100 text-blue-700',
      'already-reconciled': 'bg-gray-100 text-gray-500',
      'no-tracking': 'bg-amber-100 text-amber-700',
      'no-aa-order-id': 'bg-amber-100 text-amber-700',
      'aa-order-missing-external-ref': 'bg-amber-100 text-amber-700',
      'order-not-found': 'bg-amber-100 text-amber-700',
      'order-pending': 'bg-amber-100 text-amber-700',
      'order-not-payable': 'bg-amber-100 text-amber-700',
      'order-cancelled': 'bg-red-100 text-red-700',
      'order-refunded': 'bg-red-100 text-red-700',
      'order-delivered': 'bg-purple-100 text-purple-700',
      'order-wrong-state': 'bg-amber-100 text-amber-700',
      'race-skipped': 'bg-gray-100 text-gray-500',
      'error': 'bg-red-100 text-red-700',
    };
    return map[d] || 'bg-gray-100 text-gray-500';
  };

  const counters = lastResult?.counters || {};
  const results = lastResult?.results || [];
  const shownResults = showAllResults ? results : results.slice(0, 10);

  return (
    <div data-testid="aa-reconcile-panel" className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
        <PackageCheck className="w-4 h-4 text-indigo-600" />
        <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Shipment Reconciliation <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-mono">AA v3</span></h2>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-xs text-gray-500">
          Pull recent AA v3 shipments and update local orders with tracking + status. Dry-run
          shows exactly what would change without writing. Safe to re-run — already-reconciled
          orders are detected and skipped.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Since (ISO8601, optional)</label>
            <input
              data-testid="aa-reconcile-since-input"
              type="text" value={since} onChange={(e) => setSince(e.target.value)}
              placeholder="2026-01-01T00:00:00Z"
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-mono w-64"
            />
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 pb-2">
            <input
              data-testid="aa-reconcile-dryrun-checkbox"
              type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}
              className="w-4 h-4 accent-indigo-600"
            />
            Dry run (no writes)
          </label>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Limit</label>
            <input
              data-testid="aa-reconcile-limit-input"
              type="number" min={1} max={500} value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(500, parseInt(e.target.value || '50', 10))))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-mono w-24"
            />
          </div>
          <button
            data-testid="aa-reconcile-run-btn"
            disabled={!connectorOk || running}
            onClick={run}
            className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold disabled:opacity-50 hover:bg-indigo-700 flex items-center gap-1.5"
          >
            <PlayCircle className={`w-3.5 h-3.5 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Running…' : dryRun ? 'Run dry-run' : 'Run & write'}
          </button>
          {!connectorOk && (
            <span className="text-xs text-amber-600 pb-2">Connector not ready — configure AA in System &amp; Connectors.</span>
          )}
        </div>

        {lastResult && (
          <div data-testid="aa-reconcile-result" className="border-t border-gray-100 pt-4">
            <div className="flex items-start gap-2 mb-3">
              {lastResult.ok ? <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-600 mt-0.5" />}
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {lastResult.dryRun ? 'Dry-run complete' : 'Reconciliation complete'}
                  <span className="ml-2 text-xs text-gray-400 font-normal">{lastResult.message}</span>
                </p>
                {lastResult.truncated && (
                  <p className="mt-0.5 text-xs text-amber-700">
                    Truncated: processed {lastResult.limit} of {lastResult.totalAvailable} available shipments. Narrow <code className="font-mono">since</code> or raise the limit.
                  </p>
                )}
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-600">
                  <span>Fetched: <span className="font-semibold text-gray-900">{counters.fetched ?? 0}</span></span>
                  <span className="text-green-700">Updated: <span className="font-semibold">{counters.updated ?? 0}</span></span>
                  <span className="text-blue-700">Tracking refreshed: <span className="font-semibold">{counters.trackingUpdated ?? 0}</span></span>
                  <span className="text-gray-500">Already: <span className="font-semibold">{counters.alreadyReconciled ?? 0}</span></span>
                  <span className="text-amber-700">Skipped: <span className="font-semibold">{counters.skipped ?? 0}</span></span>
                  <span className="text-red-700">Errors: <span className="font-semibold">{counters.errors ?? 0}</span></span>
                </div>
              </div>
            </div>

            {results.length > 0 && (
              <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                <table className="w-full text-xs">
                  <thead className="text-[10px] text-gray-400 uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">Order #</th>
                      <th className="px-3 py-2 text-left">AA Order</th>
                      <th className="px-3 py-2 text-left">Tracking</th>
                      <th className="px-3 py-2 text-left">Decision</th>
                      <th className="px-3 py-2 text-left">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {shownResults.map((r, i) => (
                      <tr key={`${r.aaOrderId}-${r.trackingNumber}-${i}`} className="bg-white">
                        <td className="px-3 py-1.5 font-mono font-semibold text-gray-900">{r.orderNumber || '—'}</td>
                        <td className="px-3 py-1.5 font-mono text-gray-500">{r.aaOrderId || '—'}</td>
                        <td className="px-3 py-1.5 font-mono text-gray-700">{r.trackingNumber || '—'}</td>
                        <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${decisionBadge(r.decision)}`}>{r.decision}</span></td>
                        <td className="px-3 py-1.5 text-gray-500">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {results.length > shownResults.length && (
                  <button
                    data-testid="aa-reconcile-show-all"
                    onClick={() => setShowAllResults(true)}
                    className="w-full text-center py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
                  >
                    Show all {results.length} results
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Recent runs</h3>
            <button onClick={loadRuns} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1">
              <RefreshCw className={`w-3 h-3 ${loadingRuns ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {runs.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No runs recorded yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] text-gray-400 uppercase">
                <tr>
                  <th className="px-2 py-1.5 text-left">Started</th>
                  <th className="px-2 py-1.5 text-left">Mode</th>
                  <th className="px-2 py-1.5 text-right">Fetched</th>
                  <th className="px-2 py-1.5 text-right">Updated</th>
                  <th className="px-2 py-1.5 text-right">Refreshed</th>
                  <th className="px-2 py-1.5 text-right">Already</th>
                  <th className="px-2 py-1.5 text-right">Skipped</th>
                  <th className="px-2 py-1.5 text-right">Errors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {runs.map((r, i) => (
                  <tr key={r.runId || r.startedAt || i} className="hover:bg-gray-50">
                    <td className="px-2 py-1.5 font-mono text-gray-700">{r.startedAt?.slice(0, 19).replace('T', ' ') || '—'}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${r.dryRun ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{r.dryRun ? 'dry-run' : 'committed'}</span>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.counters?.fetched ?? 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-green-700">{r.counters?.updated ?? 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-blue-700">{r.counters?.trackingUpdated ?? 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-gray-500">{r.counters?.alreadyReconciled ?? 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-amber-700">{r.counters?.skipped ?? 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-red-700">{r.counters?.errors ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

    </div>
  );
}


// ─── Bucket-1: Inactive products holding AA shippable stock ────────────
//
// Read-only commercial-readiness audit. Surfaces dark catalogue
// products that nevertheless hold AA stock, with a per-row missing-
// fields breakdown and a *suggested* operator action — never an
// automated one. There are no bulk-action buttons, no activate
// button, no copy generation. The only links lead to the existing
// Admin > Product edit page.

const ACTION_META = {
  READY_TO_REVIEW_FOR_ACTIVATION: { label: 'Ready to review', tone: 'emerald' },
  NEEDS_EDITORIAL_BEFORE_ACTIVATION: { label: 'Needs editorial', tone: 'amber' },
  KEEP_INACTIVE_FOR_NOW: { label: 'Keep inactive', tone: 'gray' },
  OPS_ONLY_OR_DISPOSAL_REVIEW: { label: 'Ops / disposal', tone: 'red' },
  EXCLUDED_OPS_BUCKET: { label: 'Excluded (ops bucket)', tone: 'gray' },
};

const TONE_CLASS = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  gray: 'bg-gray-50 border-gray-200 text-gray-700',
  red: 'bg-red-50 border-red-200 text-red-700',
};

function Bucket1Panel({ bucket1, loading, filter, setFilter, onLoad }) {
  const summary = bucket1?.summary || {};
  const products = bucket1?.products || [];
  const filtered = filter === 'all'
    ? products
    : products.filter(p => p.suggestedAction === filter);
  return (
    <div data-testid="aa-bucket1-panel" className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Inactive products with AA stock</h3>
          <p className="text-[11px] text-gray-500 mt-0.5 max-w-2xl">
            Read-only commercial-readiness audit. Lists dark catalogue products that
            nevertheless hold AA shippable stock, with a per-row missing-fields breakdown
            and a <em>suggested</em> operator action. Nothing is mutated automatically —
            activation, slug edits, copy edits, image uploads, and category assignment
            stay manual via Admin&nbsp;&gt;&nbsp;Products.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="bucket1-load-btn"
            onClick={onLoad}
            disabled={loading}
            className="px-2.5 py-1 text-[11px] rounded bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50"
          >
            {loading ? 'Loading…' : (bucket1 ? 'Refresh' : 'Load audit')}
          </button>
        </div>
      </div>

      {bucket1?.error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">
          {bucket1.error}
        </div>
      )}

      {bucket1 && bucket1.ok && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3 text-xs">
            {[
              ['totalBucket1', 'Total', 'gray'],
              ['totalAaUnitsLocked', 'AA units locked', 'amber'],
              ['ready', 'Ready', 'emerald'],
              ['needsEditorial', 'Needs editorial', 'amber'],
              ['keepInactive', 'Keep inactive', 'gray'],
              ['opsOnly', 'Ops / disposal', 'red'],
            ].map(([k, label, tone]) => (
              <div
                key={k}
                data-testid={`bucket1-stat-${k}`}
                className={`border rounded px-2 py-1 ${TONE_CLASS[tone]}`}
              >
                <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
                <div className="font-mono font-bold text-base mt-0.5">{summary[k] ?? 0}</div>
              </div>
            ))}
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-1 flex-wrap mb-2 text-[11px]">
            <span className="text-gray-500">Filter:</span>
            {['all', 'READY_TO_REVIEW_FOR_ACTIVATION', 'NEEDS_EDITORIAL_BEFORE_ACTIVATION', 'KEEP_INACTIVE_FOR_NOW', 'OPS_ONLY_OR_DISPOSAL_REVIEW'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2 py-0.5 rounded border ${filter === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              >
                {f === 'all' ? 'All' : (ACTION_META[f]?.label || f)}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-[11px]">
              <thead className="bg-gray-50 text-gray-600 uppercase text-[10px]">
                <tr>
                  <th className="px-2 py-1.5 text-left">Product</th>
                  <th className="px-2 py-1.5 text-left">SKU</th>
                  <th className="px-2 py-1.5 text-right">AA shippable</th>
                  <th className="px-2 py-1.5 text-right">Price</th>
                  <th className="px-2 py-1.5 text-center">Image</th>
                  <th className="px-2 py-1.5 text-center">NL content</th>
                  <th className="px-2 py-1.5 text-center">Category</th>
                  <th className="px-2 py-1.5 text-left">Slug</th>
                  <th className="px-2 py-1.5 text-left">Action</th>
                  <th className="px-2 py-1.5 text-left">Missing</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((p) => {
                  const tone = ACTION_META[p.suggestedAction]?.tone || 'gray';
                  return (
                    <tr key={p.productId} data-testid={`bucket1-row-${p.sku}`} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 max-w-xs truncate text-gray-800">{p.name_nl || <span className="italic text-gray-400">no name</span>}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-600">{p.sku}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold">{p.aaShippable}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{p.price != null && p.price > 0 ? `€${Number(p.price).toFixed(2)}` : <span className="text-red-600">—</span>}</td>
                      <td className="px-2 py-1.5 text-center">{p.presence?.imageUrl ? '✓' : <span className="text-red-600">✗</span>}</td>
                      <td className="px-2 py-1.5 text-center">{(p.presence?.description_nl || p.presence?.usageHtml_nl || p.presence?.compositionHtml_nl) ? '✓' : <span className="text-red-600">✗</span>}</td>
                      <td className="px-2 py-1.5 text-center">{p.presence?.category ? '✓' : <span className="text-red-600">✗</span>}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-500">
                        {p.slug || <span className="text-gray-400 italic">—</span>}
                        {p.slugIsPlaceholder && <span className="ml-1 text-amber-700 text-[10px]">(placeholder)</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] ${TONE_CLASS[tone]}`}>
                          {ACTION_META[p.suggestedAction]?.label || p.suggestedAction}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-amber-800">{(p.missingFields || []).join(', ') || <span className="text-emerald-700">—</span>}</td>
                      <td className="px-2 py-1.5">
                        <Link
                          to={`/admin/products/${p.productId}/edit`}
                          className="text-blue-700 hover:underline text-[11px]"
                          data-testid={`bucket1-edit-link-${p.sku}`}
                        >
                          Edit →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-2 py-3 text-center text-gray-400 italic">
                      No products match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="text-[10px] text-gray-400 mt-2">
            AA snapshot: {summary.aaPagesFetched ?? '?'} page(s), {summary.aaRawRowCount ?? '?'} raw rows.
            Suggested actions are advisory; no bulk activation, no copy generation, no slug rewrites.
          </p>
        </>
      )}

      {!bucket1 && !loading && (
        <p className="text-xs text-gray-500">Click <strong>Load audit</strong> to fetch fresh AA data and run the read-only audit.</p>
      )}
    </div>
  );
}
